import { ChevronDown } from 'lucide-react';
import type { JSX } from 'react';
import { Link, NavLink } from 'react-router';
import { sourceMeta } from '../coverage';
import { formatCount } from '../format';
import { categoryPath, NOT_AN_INCIDENT, navCategoryLabel } from '../labels';
import { useMeta } from '../useStats';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

/**
 * Traffic sits second: it is the flagship page and the freshest series (ux-spec §1). Everything
 * else the corpus holds is reached through "Muud", which is built from the API, never from a list.
 */
const NAV = [
  { to: '/', label: 'Avaleht', end: true },
  { to: '/liiklusonnetused', label: 'Liiklusõnnetused', end: false },
  { to: '/vargused', label: 'Vargused', end: false },
  { to: '/kelmused', label: 'Kelmused', end: false },
  { to: '/lahisuhtevagivald', label: 'Lähisuhtevägivald', end: false },
] as const;

const PRIMARY_PATHS = new Set<string>(NAV.map((item) => item.to));

const LINK =
  'shrink-0 border-b-2 border-transparent pt-1.5 pb-2.5 text-body font-medium whitespace-nowrap text-ink-muted hover:text-ink';
const ACTIVE = 'border-accent font-semibold text-ink';

export function SiteNav(): JSX.Element {
  const { data: meta } = useMeta();
  // The digest's own category block, exactly as the landing page counts it: a category whose page
  // would be empty is not offered, and one that has earned a nav item of its own is not repeated.
  const digest = sourceMeta(meta, 'digest');
  const rest = (digest?.categories ?? meta?.categories ?? []).filter(
    (c) => c.category !== NOT_AN_INCIDENT && !PRIMARY_PATHS.has(categoryPath(c.category)),
  );

  return (
    <nav
      // The scrollbar is suppressed, so below `sm` the only cue that "Muud" is off the right edge
      // is the fade the spec asks for (ui-direction §3); the nav never truncates.
      className="-mb-px flex gap-5 overflow-x-auto pt-2.5 [-ms-overflow-style:none] [scrollbar-width:none] max-sm:[mask-image:linear-gradient(to_right,black_calc(100%-1.75rem),transparent)] [&::-webkit-scrollbar]:hidden"
      aria-label="Peamenüü"
    >
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => (isActive ? `${LINK} ${ACTIVE}` : LINK)}
        >
          {item.label}
        </NavLink>
      ))}

      {rest.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger className={`${LINK} inline-flex items-center gap-1`}>
            Muud
            <ChevronDown className="size-3.5" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[70vh] overflow-y-auto">
            {rest.map((c) => (
              <DropdownMenuItem key={c.category} asChild>
                <Link to={categoryPath(c.category)} className="flex justify-between gap-6">
                  {navCategoryLabel(c.category)}
                  <span className="text-ink-muted tabular-nums">{formatCount(c.count)}</span>
                </Link>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </nav>
  );
}
